import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { FiArrowRight, FiBarChart2, FiGrid, FiTool } from "react-icons/fi";
import { useEntitlements } from "../../components/DataContext/EntitlementsContext";

const sections = [
  { id: "portfolio", label: "Portfolio Landing", description: "Find assets and review portfolio performance", icon: FiGrid },
  { id: "asset-dashboard", label: "Asset Dashboard", description: "Estimated Settlements, volume, and price", icon: FiBarChart2 },
  { id: "outages", label: "Outage Management", description: "Find, create, and update outages", icon: FiTool },
] as const;

type GuideSection = typeof sections[number]["id"];

const UserGuide: React.FC = () => {
  const { entitlements } = useEntitlements();
  const hasOutageAccess = entitlements.some(
    (entitlement) => entitlement.po_path?.toLowerCase().trim() === "/outage-management"
  );
  const availableSections = sections.filter(
    (section) => section.id !== "outages" || hasOutageAccess
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedSection = searchParams.get("section");
  const activeSection = availableSections.find((section) => section.id === requestedSection) ?? availableSections[0];
  const [search, setSearch] = useState("");
  const matchingSections = availableSections.filter((section) =>
    `${section.label} ${section.description}`.toLowerCase().includes(search.toLowerCase())
  );
  const selectSection = (section: GuideSection) => {
    setSearchParams({ section });
  };

  return (
    <div className="flex flex-col gap-6 p-4 sm:p-6 lg:flex-row">
      <nav aria-label="User guide sections" className="w-full shrink-0 lg:sticky lg:top-4 lg:w-72 lg:self-start">
        <label htmlFor="guide-search" className="mb-2 block text-sm font-medium text-gray-700">Find a topic</label>
        <input
          id="guide-search"
          type="search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          className="mb-4 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-red-400 focus:outline-none focus:ring-2 focus:ring-red-100"
        />
        <ul className="space-y-1">
          {matchingSections.map((section) => (
            <li key={section.id}>
              <button
                type="button"
                onClick={() => selectSection(section.id)}
                aria-current={activeSection.id === section.id ? "page" : undefined}
                className={`flex w-full items-start gap-3 rounded-md px-3 py-2.5 text-left transition ${
                  activeSection.id === section.id
                    ? "bg-red-50 text-red-700 ring-1 ring-inset ring-red-200"
                    : "text-gray-700 hover:bg-gray-100"
                }`}
              >
                <section.icon className="mt-0.5 shrink-0" aria-hidden="true" />
                <span className="min-w-0">
                  <span className="block text-sm font-medium">{section.label}</span>
                  <span className="block text-xs text-gray-500">{section.description}</span>
                </span>
              </button>
            </li>
          ))}
        </ul>
        {matchingSections.length === 0 && <p className="text-sm text-gray-500">No matching topics.</p>}
        <p className="mt-6 border-t border-gray-200 pt-4 text-xs leading-relaxed text-gray-500">
          Available assets, data, and actions depend on your access. Contact your administrator if a required page or action is unavailable.
        </p>
      </nav>

      <article className="min-w-0 flex-1 text-sm leading-7 text-gray-700" aria-labelledby="guide-heading">
        <h2 id="guide-heading" className="text-xl font-semibold text-gray-800">{activeSection.label}</h2>
        <p className="mb-6 text-gray-500">{activeSection.description}</p>

        {activeSection.id === "portfolio" && (
          <>
            <p>Portfolio Landing is the starting point for reviewing your assets and comparing their performance. Open it from the main navigation, then select an asset to investigate its detailed results.</p>
            <h3 className="mb-2 mt-6 text-base font-semibold text-gray-800">Find and open an asset</h3>
            <ol className="list-decimal space-y-2 pl-5">
              <li>Choose <strong>Portfolio Landing</strong> in the top navigation.</li>
              <li><strong>Search</strong> to find an asset by name, iso, fuel type</li>
              <li><strong>Filters</strong> narrow the portfolio by asset type or ISO, or change sorting and grouping. Apply your filter choices.</li>
              <li>Switch between the grid and list views using the view icons. Use refresh to request updated portfolio data.</li>
              <li>Click on a card to open the Asset Dashboard.</li>
            </ol>
            <div className="mt-6 flex flex-wrap gap-4 border-t border-gray-200 pt-4">
              <Link to="/landing" className="inline-flex items-center gap-2 font-medium text-red-600 hover:underline">Open Portfolio Landing <FiArrowRight /></Link>
              <button type="button" onClick={() => selectSection("asset-dashboard")} className="inline-flex items-center gap-2 font-medium text-gray-700 hover:underline">Continue to Asset Dashboard <FiArrowRight /></button>
            </div>
          </>
        )}

        {activeSection.id === "asset-dashboard" && (
          <>
            <p>The Asset Dashboard shows the selected asset's results, with summary totals above the filters and detailed views below.</p>
            <h3 className="mb-2 mt-6 text-base font-semibold text-gray-800">Set the scope of your analysis</h3>
            <ol className="list-decimal space-y-2 pl-5">
              <li>Confirm the asset name, ISO, and asset type at the top of the page.</li>
              <li>Choose a <strong>Revenue Group</strong> to drill into different markets (DA or RT, Energy or Ancillary), and the <strong>Date Range</strong> you want to review.</li>
              <li>Set time <strong>Granularity</strong> and drill down into the <strong>Resources</strong>. ALL includes the available resources for the selected asset.</li>
              <li>Review <strong>Interval Alignment</strong> (Beginning, Industry, or Ending). Use its help icon to check how timestamps are presented.</li>
              <li>Use <strong>Aggregate Hourly</strong> to look at the time range (days or months) aggregated to the hours in a day (1-24)</li>
              <li>Select <strong>Apply</strong>, wait for the results, and check the displayed dates before comparing charts or totals.</li>
            </ol>
            <h3 className="mb-2 mt-6 text-base font-semibold text-gray-800">Choose a detailed view</h3>
            <dl className="divide-y divide-gray-200 border-y border-gray-200">
              <div className="py-4"><dt className="font-semibold text-gray-800">Estimated Settlements</dt><dd>Review estimated settlement results over time and the cumulative revenue series. The Generation &amp; Awards chart and settlement table provide additional detail. Hover over chart points to inspect values for a particular interval. These are estimates, not a final settlement statement.</dd></div>
              <div className="py-4"><dt className="font-semibold text-gray-800">Estimated Settlement Summary</dt><dd>Review the summarized settlement results in a blance sheet style format. Use this alongside Estimated Settlements to move between the overall result and the interval-level pattern.</dd></div>
              <div className="py-4"><dt className="font-semibold text-gray-800">Volume</dt><dd>Review the asset's available volume series over the selected period. Compare changes over time and check the units, resources, and granularity shown.</dd></div>
              <div className="py-4"><dt className="font-semibold text-gray-800">Price</dt><dd>Review the available price series for the selected period. Compare price movements with volume and settlement results using the same date range and alignment.</dd></div>
            </dl>
            <div className="mt-6 flex flex-wrap gap-4 border-t border-gray-200 pt-4">
              <Link to="/landing" className="inline-flex items-center gap-2 font-medium text-red-600 hover:underline">Choose an asset <FiArrowRight /></Link>
              <Link to="/knowledge-center/metric" className="inline-flex items-center gap-2 font-medium text-gray-700 hover:underline">Open Metric Dictionary <FiArrowRight /></Link>
            </div>
          </>
        )}

        {activeSection.id === "outages" && (
          <>
            <p>Use Outage Management to review outage records and maintain the details of planned or ongoing asset unavailability. Open <strong>Outage Management</strong> from the main navigation.</p>
            <h3 className="mb-2 mt-6 text-base font-semibold text-gray-800">Find and review an outage</h3>
            <ol className="list-decimal space-y-2 pl-5">
              <li>Review the outage table and its active filters. The default status selection includes <strong>Active</strong> and <strong>Approved</strong>; change the status filter when looking for other records.</li>
              <li>Use the table's search and column filters to narrow the results. If an expected record is missing, review the filters before creating another outage.</li>
              <li>Select an outage to inspect its details in the form. Check the asset, resource, start and end times, status, and workflow.</li>
              <li>Review the selected outage's comments for its recorded details and ISO-ID information.</li>
            </ol>
            <h3 className="mb-2 mt-6 text-base font-semibold text-gray-800">Create an outage</h3>
            <ol className="list-decimal space-y-2 pl-5">
              <li>Use the blank form, or choose <strong>New</strong> when viewing an existing record, if your access allows it. <strong>Clear</strong> resets a new form.</li>
              <li>Select the correct <strong>Asset/Resource</strong>, then complete the required fields marked with an asterisk.</li>
              <li>Enter the outage start and end times carefully. The form uses Eastern wall-clock time. Confirm the duration and any capacity or outage details before continuing.</li>
              <li>Review the status and workflow. A new outage's status follows its time window unless you explicitly choose a status.</li>
              <li>Add a comment with the outage details and the required <strong>ISO-ID</strong> of at least six characters. If a duplicate-record warning appears, review the existing outage rather than repeatedly submitting the same details.</li>
              <li>Select <strong>Create</strong> and review the confirmation dialog. Confirm only after checking the entered details, then verify that the record appears under the appropriate filters.</li>
            </ol>
            <h3 className="mb-2 mt-6 text-base font-semibold text-gray-800">Update and verify</h3>
            <p>Select the existing outage, choose <strong>Edit</strong> when available, and update the necessary fields. Select <strong>Save</strong> and confirm the change, or choose <strong>Cancel</strong> to leave edit mode without saving. Check the refreshed record to verify the result. If a validation message appears, correct the highlighted fields and try again.</p>
            <h3 className="mb-2 mt-6 text-base font-semibold text-gray-800">Copy an existing outage</h3>
            <p>Select an outage and choose <strong>Copy</strong> to prepare a new entry using its details. Pick the new <strong>Asset/Resource</strong>, review the dates, status, workflow, comments, and ISO-ID, then select <strong>Create</strong>. Copy does not update the original record.</p>
            <p className="mt-3">Use the table's export actions when you need an offline copy. Check the selected filters and the exported contents before sharing the file.</p>
            <div className="mt-6 border-t border-gray-200 pt-4">
              <Link to="/outage-management" className="inline-flex items-center gap-2 font-medium text-red-600 hover:underline">Open Outage Management <FiArrowRight /></Link>
            </div>
          </>
        )}
      </article>
    </div>
  );
};

export default UserGuide;